import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../../components/ui/Navbar";
import { toast } from "react-toastify";
import {
  Info,
  Cpu,
  Wrench,
  History,
  BookOpen,
  MapPin,
  ArrowLeft,
  LoaderPinwheel,
  Building2,
} from "lucide-react";
import AccesorioModal from "../../components/ui/AccesoriosForm";

import FichaTecnica from "./Tabs/FichaTecnica";
import Componentes from "./Tabs/Componentes";
import Mantenimientos from "./Tabs/Mantenimientos";
import Historico from "./Tabs/Historico";
import Documentos from "./Tabs/Documentos";
import MovimientosEquipo from "./Tabs/MovimientosEquipo";

const CONDICION_BADGE = {
  Bueno: "bg-green-50 text-green-700 border border-green-200",
  Regular: "bg-amber-50 text-amber-700 border border-amber-200",
  Malo: "bg-red-50 text-red-700 border border-red-200",
  "Fuera de servicio": "bg-gray-100 text-gray-500 border border-gray-200",
};

const PublicTopBar = () => (
  <header className="w-full bg-white shadow-md border-b fixed top-0 z-50">
    <div className="flex items-center justify-center px-4 md:px-6 py-3">
      <img
        src="/logo.png"
        alt="Previonix Logo"
        className="h-14 w-auto object-contain"
      />
    </div>
  </header>
);

const TABS = [
  { key: "ficha", label: "Ficha técnica", icon: Info },
  { key: "componentes", label: "Componentes", icon: Cpu },
  { key: "mantenimiento", label: "Mantenimiento", icon: Wrench },
  { key: "historico", label: "Histórico", icon: History },
  { key: "documentos", label: "Documentación", icon: BookOpen },
  { key: "movimientos", label: "Movimientos", icon: MapPin },
];

const EquipoProfilePage = ({ publicView = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [equipo, setEquipo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ficha");

  const [showAccesorioModal, setShowAccesorioModal] = useState(false);
  const [selectedAccesorioId, setSelectedAccesorioId] = useState(null);

  const [mantenimientoInfo, setMantenimientoInfo] = useState(null);
  const [accesoriosInfo, setAccesoriosInfo] = useState([]);
  const [loadingAccesorios, setLoadingAccesorios] = useState(false);

  const [publicPayload, setPublicPayload] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const totalPages = Math.ceil((accesoriosInfo?.length || 0) / pageSize);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const fetchMantenimiento = useCallback(
    async (marcaId, modeloId) => {
      if (publicView) return;
      try {
        const { data } = await axios.get(
          `${API}/mantenimiento/${marcaId}/${modeloId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setMantenimientoInfo(data);
      } catch {
        setMantenimientoInfo(null);
      }
    },
    [API, token, publicView],
  );

  const fetchAccesorios = useCallback(
    async (marcaId, modeloId, equipoId = null) => {
      if (publicView) return;
      setLoadingAccesorios(true);
      try {
        const url = equipoId
          ? `${API}/accesorios/${marcaId}/${modeloId}?equipoId=${equipoId}`
          : `${API}/accesorios/${marcaId}/${modeloId}`;
        const { data } = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAccesoriosInfo(data || []);
      } catch {
        setAccesoriosInfo([]);
      } finally {
        setLoadingAccesorios(false);
      }
    },
    [API, token, publicView],
  );

  useEffect(() => {
    const fetchEquipo = async () => {
      try {
        if (publicView) {
          const headers = token
            ? { Authorization: `Bearer ${token}` }
            : undefined;
          const res = await axios.get(`${API}/public/equipos/${id}`, {
            headers,
          });

          if (token) {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const userCompanyId =
              user?.empresa?._id?.toString?.() || user?.empresa?.toString?.();
            const equipoCompanyId =
              res.data?.equipo?.cliente?.empresa?._id?.toString?.() ||
              res.data?.equipo?.cliente?.empresa?.toString?.();

            if (
              userCompanyId &&
              equipoCompanyId &&
              userCompanyId === equipoCompanyId
            ) {
              navigate(`/equipos/${id}`, { replace: true });
              return;
            }
          }

          setPublicPayload(res.data);
          setEquipo(res.data?.equipo || null);
          setMantenimientoInfo(res.data?.mantenimiento || null);
          setAccesoriosInfo(res.data?.componentes || []);
          return;
        }

        const res = await axios.get(`${API}/equipos/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEquipo(res.data);

        if (res.data?.marca?._id && res.data?.modelo?._id) {
          fetchMantenimiento(res.data.marca._id, res.data.modelo._id);
          fetchAccesorios(
            res.data.marca._id,
            res.data.modelo._id,
            res.data._id,
          );
        }
      } catch (error) {
        if (error?.response?.status === 403) {
          if (publicView) {
            if (token) {
              toast.error(
                error?.response?.data?.message ||
                  "No tienes permiso para ver esta ficha de equipo",
              );
              navigate("/dashboard", { replace: true });
              return;
            }
            navigate("/login", {
              replace: true,
              state: { from: { pathname: `/open/equipo/${id}` } },
            });
            return;
          }
          toast.error(
            error?.response?.data?.message ||
              "No tienes permiso para ver esta ficha de equipo",
          );
          navigate("/dashboard", { replace: true });
          return;
        }
        toast.error("Error al cargar equipo");
      } finally {
        setLoading(false);
      }
    };

    fetchEquipo();
  }, [
    id,
    API,
    token,
    fetchMantenimiento,
    fetchAccesorios,
    publicView,
    navigate,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {publicView ? <PublicTopBar /> : <Navbar />}
        <div className="pt-20 md:pt-28 pb-8">
          <div className="container mx-auto px-4">
            <div className="inline-flex items-center gap-2.5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600 shrink-0" />
              Cargando datos, por favor espera…
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!equipo) {
    return (
      <div className="min-h-screen bg-gray-50">
        {publicView ? <PublicTopBar /> : <Navbar />}
        <div className="pt-20 md:pt-28 pb-8">
          <div className="container mx-auto px-4">
            <div className="inline-flex rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              No fue posible cargar la ficha del equipo.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const locationParts = [
    equipo.cliente?.name,
    equipo.sede?.name,
    equipo.area?.name,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      {publicView ? <PublicTopBar /> : <Navbar />}

      <div className="pt-20 md:pt-28 pb-8">
        <div className="container mx-auto px-4 space-y-3">
          {/* ── Equipment hero card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-5">
              {/* Equipment image */}
              {equipo.modelo?.img && (
                <div className="shrink-0 w-20 h-20 rounded-xl border border-gray-100 bg-gray-50 overflow-hidden flex items-center justify-center">
                  <img
                    src={equipo.modelo.img}
                    alt="Modelo"
                    className="object-contain w-full h-full"
                  />
                </div>
              )}

              {/* Identity */}
              <div className="flex-1 min-w-0">
                {/* Badges */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {equipo.categoria?.name && (
                    <span className="inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs px-2.5 py-0.5 font-medium">
                      {equipo.categoria.name}
                    </span>
                  )}
                  {equipo.condicion && (
                    <span
                      className={`inline-flex items-center rounded-full text-xs px-2.5 py-0.5 font-medium ${
                        CONDICION_BADGE[equipo.condicion] ||
                        "bg-gray-100 text-gray-500 border border-gray-200"
                      }`}
                    >
                      {equipo.condicion}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                  {equipo.marca?.name} · {equipo.modelo?.name}
                </h1>

                {/* Code & serial */}
                <div className="flex flex-wrap gap-x-5 gap-y-0.5 mt-1 text-sm text-gray-500">
                  <span>
                    <span className="font-medium text-gray-700">Código:</span>{" "}
                    {equipo.codigo || "—"}
                  </span>
                  <span>
                    <span className="font-medium text-gray-700">Serial:</span>{" "}
                    {equipo.serial || "—"}
                  </span>
                </div>

                {/* Location */}
                {locationParts.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-sm text-gray-400">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    <span>{locationParts.join(" · ")}</span>
                  </div>
                )}
              </div>

              {/* Back button */}
              {!publicView && (
                <button
                  onClick={() => navigate(-1)}
                  className="shrink-0 self-start inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Volver
                </button>
              )}
            </div>
          </div>

          {/* ── Tabs + content ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Tab bar */}
            <div className="border-b border-gray-100 overflow-x-auto">
              <div className="flex min-w-max">
                {TABS.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === key
                        ? "border-blue-600 text-blue-600 bg-blue-50/40"
                        : "border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div className="p-5 sm:p-6">
              {activeTab === "ficha" && (
                <FichaTecnica
                  equipo={equipo}
                  mantenimiento={mantenimientoInfo}
                  publicView={publicView}
                />
              )}

              {activeTab === "componentes" && (
                <Componentes
                  accesoriosInfo={accesoriosInfo}
                  page={page}
                  setPage={setPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  totalPages={totalPages}
                  loading={loadingAccesorios}
                  token={token}
                  API={API}
                  fetchAccesorios={() =>
                    fetchAccesorios(
                      equipo.marca._id,
                      equipo.modelo._id,
                      equipo._id,
                    )
                  }
                  setShowAccesorioModal={setShowAccesorioModal}
                  setSelectedAccesorioId={setSelectedAccesorioId}
                />
              )}

              {activeTab === "mantenimiento" && (
                <Mantenimientos
                  equipoId={equipo._id}
                  publicView={publicView}
                  initialMantenimientos={publicPayload?.mantenimientos || []}
                />
              )}

              {activeTab === "historico" && (
                <Historico
                  equipo={equipo}
                  publicView={publicView}
                  initialGestiones={publicPayload?.historico?.gestiones || []}
                  initialPredictivos={
                    publicPayload?.historico?.predictivos || []
                  }
                />
              )}

              {activeTab === "documentos" && (
                <Documentos
                  equipo={equipo}
                  publicView={publicView}
                  initialDocumentos={publicPayload?.documentos || []}
                />
              )}

              {activeTab === "movimientos" && (
                <MovimientosEquipo
                  equipo={equipo}
                  clienteId={equipo.cliente?._id}
                  publicView={publicView}
                  initialMovimientos={publicPayload?.movimientos || []}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {!publicView && showAccesorioModal && (
        <AccesorioModal
          accesorioId={selectedAccesorioId}
          marcaId={equipo.marca?._id}
          modeloId={equipo.modelo?._id}
          equipoId={equipo._id}
          onClose={() => {
            setShowAccesorioModal(false);
            setSelectedAccesorioId(null);
            fetchAccesorios(equipo.marca._id, equipo.modelo._id, equipo._id);
          }}
        />
      )}
    </div>
  );
};

export default EquipoProfilePage;
